/**
 * Pro Feature Access Utilities
 * Ensures Pro Free Trial users get full access to all Pro features
 */

/**
 * Check if user has Pro access (including Free Trial)
 * @param {string} subscriptionPlan - User's subscription plan
 * @param {string} subscriptionStatus - User's subscription status
 * @returns {boolean} - True if user has Pro access
 */
export const hasProAccess = (subscriptionPlan, subscriptionStatus) => {
  const normalizedPlan = String(subscriptionPlan || '').toLowerCase();
  const normalizedStatus = String(subscriptionStatus || '').toLowerCase();

  // Pro users always have access
  if (normalizedPlan === 'pro') {
    return true;
  }
  
  // Free Trial users should have Pro access regardless of plan
  if (normalizedStatus === 'free_trial' && normalizedPlan === 'pro') {
    return true;
  }
  
  // Essential users don't have Pro access
  return false;
};

/**
 * Check if user should see Pro features in UI
 * @param {string} subscriptionPlan - User's subscription plan
 * @param {string} subscriptionStatus - User's subscription status
 * @returns {boolean} - True if user should see Pro features
 */
export const shouldShowProFeatures = (subscriptionPlan, subscriptionStatus) => {
  return hasProAccess(subscriptionPlan, subscriptionStatus);
};

/**
 * Get user's effective subscription tier for display
 * @param {string} subscriptionPlan - User's subscription plan
 * @param {string} subscriptionStatus - User's subscription status
 * @returns {string} - Effective subscription tier
 */
export const getEffectiveSubscriptionTier = (subscriptionPlan, subscriptionStatus) => {
  if (hasProAccess(subscriptionPlan, subscriptionStatus)) {
    return 'Pro';
  }
  return 'Essential';
};

/**
 * Check if user can access Chat feature
 * @param {string} subscriptionPlan - User's subscription plan
 * @param {string} subscriptionStatus - User's subscription status
 * @returns {boolean} - True if user can access chat
 */
export const canAccessChat = (subscriptionPlan, subscriptionStatus, role) => {
  // Additional users always have chat access under their parent account
  if (String(role || '').toLowerCase() === 'additional_user') return true;
  return hasProAccess(subscriptionPlan, subscriptionStatus);
};

/**
 * Check if user can access advanced features
 * @param {string} subscriptionPlan - User's subscription plan
 * @param {string} subscriptionStatus - User's subscription status
 * @returns {boolean} - True if user can access advanced features
 */
export const canAccessAdvancedFeatures = (subscriptionPlan, subscriptionStatus) => {
  return hasProAccess(subscriptionPlan, subscriptionStatus);
};

/**
 * Get subscription display text
 * @param {string} subscriptionPlan - User's subscription plan
 * @param {string} subscriptionStatus - User's subscription status
 * @returns {string} - Display text for subscription
 */
export const getSubscriptionDisplayText = (subscriptionPlan, subscriptionStatus) => {
  if (subscriptionStatus === 'free_trial' || subscriptionStatus === 'Free Trial') {
    return 'Pro Free Trial';
  }
  
  if (subscriptionPlan === 'pro' || subscriptionPlan === 'Pro') {
    return 'Pro Plan';
  }
  
  return 'Essential Plan';
};

/**
 * Safeguard: Force Pro access for specific users (emergency override)
 * @param {string} email - User's email
 * @returns {boolean} - True if user should have forced Pro access
 */
export const hasForcedProAccess = (email) => {
  return false;
};

/**
 * Main function to determine Pro access with all safeguards
 * @param {Object} user - User object
 * @returns {boolean} - True if user has Pro access
 */
export const determineProAccess = (user) => {
  if (!user) return false;
  
  const { subscriptionPlan, subscriptionStatus, email, role } = user;
  
  // Additional users inherit access to chat and collaboration features
  if (String(role || '').toLowerCase() === 'additional_user') {
    return true;
  }
  
  // Emergency override for specific users
  if (hasForcedProAccess(email)) {
    return true;
  }
  
  // Normal Pro access check
  return hasProAccess(subscriptionPlan, subscriptionStatus);
};
